import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { t } from 'i18next';
import { useMediaQueryGs } from 'app/hooks/useMediaQuery';
import {
  Container,
  FlipContainer,
  GoalCardBackground,
  InnerContainer,
  LoaderWrapper,
  PrintGoalcardContainer,
} from './styles';
import LogoHeader from 'app/components/LogoHeader';
import { goalcardService } from 'api';
import { GoalCardsByIdResponse } from 'types/goalcard';
import Confetti from 'react-confetti';
import './animation.css';
import FirstFlip from './AnimatedGoalCard/FirstFlip';
import SecondFlip from './AnimatedGoalCard/SecondFlip';
import ThirdFlip from './AnimatedGoalCard/ThirdFlip';
import { CONFFETI_COLORS } from 'utils';
import { useReactToPrint } from 'react-to-print';
import PrintGoalcard from './AnimatedGoalCard/PrintGoalcard';
import DownloadAppModal from 'app/components/Modals/DownloadAppModal';
import LottieAnimation from 'app/components/LottieAnimation';
import Images from 'assets/img/Images';
import AnimatedGoalcardErrorModal from 'app/components/Modals/AnimatedGoalcardErrorModal';

export const AnimatedGoalCard: React.FC = () => {
  const { isMobile } = useMediaQueryGs();
  const [goalId, setGoalId] = useState('');
  const [affiliateCode, setAffiliateCode] = useState('');
  const [contactAffiliateCode, setContactAffiliateCode] = useState('');
  const [confettiShouldStart, setConfettiShouldStart] =
    useState<boolean>(false);
  const [confettiShouldRecycle, setConfettiShouldRecycle] =
    useState<boolean>(false);
  const [showControls, setShowControls] = useState<boolean>(false);
  const [showDownloadModal, setShowDownloadModal] = useState<boolean>(false);
  const [animationStarted, setAnimationStarted] = useState<boolean>(false);
  const sectionAnimated: any = useRef(null);
  const componentRef: any = useRef(null);
  const contRef: any = useRef(null);
  const [goalcardData, setGoalcardData] =
    useState<GoalCardsByIdResponse | null>(null);
  const [goalcardDataLoading, setGoalcardDataLoading] =
    useState<boolean>(false);

  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [errorDescription, setErrorDescription] = useState<string>('');

  const [thirdFlipContainerHeight, setThirdFlipContainerHeight] =
    useState<number>(0);

  const getGoalIdParamFromUrl = () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const idParam = urlSearchParams.get('id');
    const affiliateCodeParam = urlSearchParams.get('code');
    const contactAffiliateCodeParam = urlSearchParams.get('ac');
    if (idParam) {
      setGoalId(idParam);
    }
    if (affiliateCodeParam) {
      setAffiliateCode(affiliateCodeParam);
    }
    if (contactAffiliateCodeParam) {
      setContactAffiliateCode(contactAffiliateCodeParam);
    }
  };

  const firstFlip = (isFromReplay: boolean) => {
    setTimeout(() => {
      setConfettiShouldStart(true);
      if (isFromReplay) {
        setConfettiShouldRecycle(true);
        setTimeout(() => {
          setConfettiShouldRecycle(false);
        }, 3000);
      }
    }, 2000);

    if (isFromReplay && sectionAnimated) {
      sectionAnimated.current.classList.add('test');
      sectionAnimated.current.classList.add('first-flip');
      setTimeout(() => {
        sectionAnimated.current.classList.remove('test');
      }, 1500);
    } else {
      sectionAnimated.current.classList.add('first-flip');
    }
  };

  const secondFlip = () => {
    sectionAnimated.current.classList.add('second-flip');
    setTimeout(() => {
      setShowControls(true);
    }, 1000);
  };

  const animateCard = () => {
    sectionAnimated.current.classList.add('animated');
    setTimeout(() => {
      firstFlip(true);
    }, 1000);

    setTimeout(() => {
      secondFlip();
    }, 5000);
  };

  const replayAnimation = () => {
    setShowControls(false);
    sectionAnimated.current.classList.add('ttreplay');

    setTimeout(() => {
      setTimeout(() => {
        sectionAnimated.current.classList.remove('first-flip');
      }, 1000);

      setTimeout(() => {
        sectionAnimated.current.classList.remove('second-flip');
      }, 1000);

      setTimeout(() => {
        sectionAnimated.current.classList.remove('ttreplay');
        animateCard();
      }, 1000);
    }, 1000);
  };

  const handleShowModal = () => setShowDownloadModal(!showDownloadModal);
  const onClaim = () => {
    if (isMobile && goalcardData?.claimURL) {
      window.location = goalcardData?.claimURL as unknown as Location;
    } else {
      handleShowModal();
    }
  };

  const onPrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const getGoalcardById = (id: string) => {
    setGoalcardDataLoading(true);
    goalcardService
      .getGoalcard(id)
      .then((res) => {
        setGoalcardData(res as GoalCardsByIdResponse);
      })
      .catch((error) => {
        const errorMessage = error.response?.data?.['status-description'];
        setShowErrorModal(true);

        if (errorMessage) {
          return setErrorDescription(errorMessage);
        }
        // Default error msg
        setErrorDescription(
          String(t('animatedGoalCard.errorModal.errorTryAgain')),
        );
      })
      .finally(() => {
        setGoalcardDataLoading(false);
      });
  };

  const getGoalcardByAffiliateCode = (code: string) => {
    setGoalcardDataLoading(true);
    goalcardService
      .getAffiliateGoalcard(code)
      .then((res) => {
        setGoalcardData(res as GoalCardsByIdResponse);
      })
      .catch((error) => {
        const errorMessage = error.response?.data?.['status-description'];
        setShowErrorModal(true);

        if (errorMessage) {
          return setErrorDescription(errorMessage);
        }
        // Default error msg
        setErrorDescription(
          String(t('animatedGoalCard.errorModal.errorTryAgain')),
        );
      })
      .finally(() => {
        setGoalcardDataLoading(false);
      });
  };

  const getGoalcardByContactCode = (contactCode: string) => {
    setGoalcardDataLoading(true);
    goalcardService
      .getContactAffiliateGoalcard(contactCode)
      .then((res) => {
        setGoalcardData(res as GoalCardsByIdResponse);
      })
      .catch((error) => {
        const errorMessage = error.response?.data?.['status-description'];
        setShowErrorModal(true);

        if (errorMessage) {
          return setErrorDescription(errorMessage);
        }
        // Default error msg
        setErrorDescription(
          String(t('animatedGoalCard.errorModal.errorTryAgain')),
        );
      })
      .finally(() => {
        setGoalcardDataLoading(false);
      });
  };

  const fetchGoalcard = () => {
    if (goalId) {
      getGoalcardById(goalId);
    } else if (affiliateCode) {
      getGoalcardByAffiliateCode(affiliateCode);
    } else if (contactAffiliateCode) {
      getGoalcardByContactCode(contactAffiliateCode);
    }
  };

  useEffect(() => {
    if (animationStarted || !goalcardData) {
      return;
    }
    animateCard();
    setAnimationStarted(true);
  }, [animationStarted, goalcardData]);

  useEffect(() => {
    getGoalIdParamFromUrl();
  }, []);

  useEffect(() => {
    if (!goalcardData && !goalcardDataLoading) {
      fetchGoalcard();
    }
  }, [goalId, affiliateCode, contactAffiliateCode]);

  useLayoutEffect(() => {
    if (contRef.current) {
      const height = contRef.current.clientHeight;
      setThirdFlipContainerHeight(height);
    }
  }, [contRef.current, contRef.current ? contRef.current.clientHeight : 0]);

  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    setErrorDescription('');
    fetchGoalcard();
  };

  return (
    <GoalCardBackground height={thirdFlipContainerHeight}>
      <PrintGoalcardContainer>
        <div ref={componentRef}>
          {goalcardData && <PrintGoalcard goalcardData={goalcardData} />}
        </div>
      </PrintGoalcardContainer>
      <AnimatedGoalcardErrorModal
        show={showErrorModal}
        description={errorDescription}
        onClose={handleCloseErrorModal}
      />
      <DownloadAppModal
        show={showDownloadModal}
        onClose={handleShowModal}
      />
      <Confetti
        id="confetti-container"
        run={confettiShouldStart}
        recycle={confettiShouldRecycle}
        colors={CONFFETI_COLORS}
        gravity={0.3}
        numberOfPieces={500}
        tweenDuration={10000}
      />
      <Container>
        <LogoHeader />
        {goalcardDataLoading && (
          <LoaderWrapper>
            <LottieAnimation animationData={Images.LOADER_JSON} />
          </LoaderWrapper>
        )}
        {!goalcardDataLoading && goalcardData && (
          <InnerContainer>
            <FlipContainer>
              <div ref={sectionAnimated}>
                <div className="flip-container">
                  <div className="flipper">
                    <div className="flip-one-side">
                      <FirstFlip whoIsCardFor={goalcardData?.whoIsCardFor} />
                    </div>
                    <div className="flip-two-side">
                      <SecondFlip
                        goalcardImg={goalcardData?.goalcardImg}
                        amount={goalcardData?.amount}
                        whoIsCardFor={goalcardData?.whoIsCardFor}
                        investorFullName={goalcardData?.investorFullName}
                        internalDescription={goalcardData?.internalDescription}
                      />
                    </div>
                    <div
                      className="flip-three-side"
                      ref={contRef}
                    >
                      <ThirdFlip
                        whoIsCardFor={goalcardData?.whoIsCardFor}
                        description={goalcardData?.description}
                        investorFullName={goalcardData?.investorFullName}
                        fullVideoURL={goalcardData?.fullVideoURL}
                        imageURL={goalcardData?.imageURL}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </FlipContainer>
          </InnerContainer>
        )}
      </Container>
      {showControls && (
        <div className="card-control">
          <button
            className="btn btn-clear section-button"
            onClick={replayAnimation}
          >
            <span className="replay-img" />
            {t('printGoalcard.replayBtn')}
          </button>
          <button
            className="btn btn-primary section-button"
            onClick={onClaim}
          >
            {t('printGoalcard.claimBtn')}
          </button>

          {!isMobile && (
            <button
              className="btn btn-clear section-button"
              onClick={onPrint}
            >
              <span className="print-img" />
              {t('printGoalcard.printBtn')}
            </button>
          )}
        </div>
      )}
    </GoalCardBackground>
  );
};
